/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { createSlice } from '@reduxjs/toolkit';
import { signInActions } from 'modules/sign-in/slice';
import { dashboardActions } from 'modules/dashboard/slice';
import { SLICE, TAGS } from 'modules/dashboard/constants';
import {
  hasAnyLevelSelected,
  applyDrillDownData,
  applyMergedLevelData,
  sortByContinent,
} from './function';

/**
 * Initial states of Pace Time widget functions are defined here
 */
export const initialState = {
  regionMapFilterList: [],
  regionMapBookingDate: null,
  regionMapChartBreakdown: null,
  regionMapFocusOn: [],
  regionalMapOtb: false,
  isRegionDetailed: false,
  regionalMapUrl: '',
  regionalBreakdownTabularView: false,
  //
  isFocusOnSet: false,
  regionIsDisplay: true,
  regionTabularIsDisplay: true,
  //
  regionalData: null,
  regionalFullData: null,
  fullDataLoading: false,
  dataLoading: false,
  regionalMapSelection: {
    continent: null,
    country: null,
    state: null,
    city: null,
  },
  regionalMapLevel: 'continent',
  isDrillDown: false,
};
/**
 * All actions related to Pace Time widget feature are defined here
 */
export const regionalMapSlice = createSlice({
  name: SLICE.REGIONAL_MAP,
  initialState,
  reducers: {
    setRegionMapFilterList(state, action) {
      state.regionMapFilterList = action?.payload;
    },

    setRegionMapBookingDate(state, action) {
      state.regionMapBookingDate = action?.payload;
    },
    setRegionMapChartBreakdown(state, action) {
      state.regionMapChartBreakdown = action?.payload;
    },
    setRegionMapFocusOn(state, action) {
      state.regionMapFocusOn = action?.payload;
    },
    setRegionalMapOtb(state, action) {
      state.regionalMapOtb = action?.payload;
    },
    setIsRegionDetailed(state, action) {
      state.isRegionDetailed = action?.payload;
    },
    setRegionalMapUrl(state, action) {
      state.regionalMapUrl = action.payload;
    },
    setRegionalBreakdownTabularView(state, action) {
      state.regionalBreakdownTabularView = action?.payload;
    },
    setIsFocusOnSet(state, action) {
      state.isFocusOnSet = action?.payload;
    },
    setRegionMapIsDisplay(state, action) {
      state.regionIsDisplay = action?.payload;
    },
    setRegionMapTabularIsDisplay(state, action) {
      state.regionTabularIsDisplay = action?.payload;
    },
    getRegionalData(state) {
      state.dataLoading = true;
    },
    setRegionalData(state, action) {
      state.regionalData = action?.payload;
    },
    getRegionalDataSucceeded(state, action) {
      state.dataLoading = false;
      if (!state.regionalData || !hasAnyLevelSelected(state.regionalMapSelection)) {
        state.regionalData = action?.payload;
      } else if (state.isDrillDown) {
        state.regionalData = applyDrillDownData(
          state.regionalData,
          action?.payload,
          state.regionalMapSelection
        );
      } else {
        state.regionalData = applyMergedLevelData(
          state.regionalData,
          action?.payload,
          state.regionalMapSelection
        );
      }
    },
    getRegionalDataFailed(state) {
      state.dataLoading = false;
    },
    getRegionalFullData(state) {
      state.fullDataLoading = true;
    },
    getRegionalFullDataSucceeded(state, action) {
      state.fullDataLoading = false;
      state.regionalFullData = sortByContinent(action?.payload);
    },
    getRegionalFullDataFailed(state) {
      state.fullDataLoading = false;
    },
    setRegionalMapSelection(state, action) {
      state.regionalMapSelection = {
        ...state.regionalMapSelection,
        ...action?.payload,
      };
    },
    setRegionalMapLevel(state, action) {
      state.regionalMapLevel = action?.payload;
    },
    setIsDrillDown(state, action) {
      state.isDrillDown = action?.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInActions.signIn, (state) => ({
        ...state,
        ...initialState,
      }))
      .addCase(dashboardActions.getVisualFiltersSucceeded, (state, action) => ({
        ...state,
        regionMapChartBreakdown: action?.payload?.results?.find(
          (filter) =>
            filter?.tags?.includes(TAGS.SEGMENT_BREAKDOWN) &&
            filter?.tags?.includes(TAGS.REGION) &&
            filter?.isDefault === true
        )?.id,
      }));
  },
});
//
export const { actions: regionalMapActions } = regionalMapSlice;
